import React, { useCallback, useEffect, useState } from "react";

import { CalendarRange, Loader2, Save } from "lucide-react";

import AlAssessmentSettingsForm, {
  AlAssessmentSettingsValues,
  allowedWindowDays,
  CADENCE_DAY_OPTIONS,
  DEFAULT_AL_ASSESSMENT_SETTINGS,
  FORMAL_DUE_DATE_OPTIONS,
  parseLevelGapThreshold,
  pickAllowed,
  GRACE_EXTRA_DAY_OPTIONS,
  REMINDER_DAY_OPTIONS,
  RESOLUTION_DAY_OPTIONS,
} from "../../Components/AlAssessmentSettingsForm";
import Button from "../../Components/Button";
import { TooltipProvider } from "../../intelligence-briefing-library/components/ui/tooltip";
import { useAppDispatch } from "../../Store/Hooks";
import {
  fetchAlAssessmentDefaults,
  updateAlAssessmentDefaults,
} from "../../Store/Reducers/AdminSlice";
import { toast } from "../../Utils/Helper/toast";

interface AlAssessmentDefaultsApi {
  cadence_days?: number;
  window_days?: number;
  window_offset_days?: number | null;
  formal_default_due_date?: number;
  formal_due_reminder_days?: number;
  team_due_reminder_days?: number;
  disconnect_reminder_days?: number;
  level_gap_threshold?: number | string;
  resolution_days?: number;
  grace_extra_days?: number;
}

const D = DEFAULT_AL_ASSESSMENT_SETTINGS;

const fromApi = (
  api?: AlAssessmentDefaultsApi | null
): AlAssessmentSettingsValues => {
  if (!api) return D;
  const cadenceDays = pickAllowed(
    api.cadence_days,
    CADENCE_DAY_OPTIONS,
    D.cadenceDays
  );
  return {
    cadenceDays,
    windowDays: allowedWindowDays(cadenceDays, api.window_days),
    windowOffsetDays: api.window_offset_days ?? null,
    formalDefaultDueDateDays: pickAllowed(
      api.formal_default_due_date,
      FORMAL_DUE_DATE_OPTIONS,
      D.formalDefaultDueDateDays
    ),
    formalReminderDays: pickAllowed(
      api.formal_due_reminder_days,
      REMINDER_DAY_OPTIONS,
      D.formalReminderDays
    ),
    teamReminderDays: pickAllowed(
      api.team_due_reminder_days,
      REMINDER_DAY_OPTIONS,
      D.teamReminderDays
    ),
    disconnectReminderDays: pickAllowed(
      api.disconnect_reminder_days,
      REMINDER_DAY_OPTIONS,
      D.disconnectReminderDays
    ),
    levelGapThreshold: parseLevelGapThreshold(api.level_gap_threshold),
    resolutionDays: pickAllowed(
      api.resolution_days,
      RESOLUTION_DAY_OPTIONS,
      D.resolutionDays
    ),
    graceExtraDays: pickAllowed(
      api.grace_extra_days,
      GRACE_EXTRA_DAY_OPTIONS,
      D.graceExtraDays
    ),
  };
};

const toApi = (
  values: AlAssessmentSettingsValues
): AlAssessmentDefaultsApi => ({
  cadence_days: values.cadenceDays,
  window_days: allowedWindowDays(values.cadenceDays, values.windowDays),
  window_offset_days: values.windowOffsetDays,
  formal_default_due_date: values.formalDefaultDueDateDays,
  formal_due_reminder_days: values.formalReminderDays,
  team_due_reminder_days: values.teamReminderDays,
  disconnect_reminder_days: values.disconnectReminderDays,
  level_gap_threshold: values.levelGapThreshold,
  resolution_days: values.resolutionDays,
  grace_extra_days: values.graceExtraDays,
});

const errorMessage = (error: unknown, fallback: string): string =>
  typeof error === "string" && error ? error : fallback;

const AdminAlAssessmentDefaultsContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const [values, setValues] = useState<AlAssessmentSettingsValues>(D);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const content = await dispatch(fetchAlAssessmentDefaults()).unwrap();
      setValues(fromApi(content as AlAssessmentDefaultsApi | null));
    } catch (error) {
      toast.error(
        errorMessage(error, "Failed to load AL assessment defaults.")
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const content = await dispatch(
        updateAlAssessmentDefaults(toApi(values))
      ).unwrap();
      if (content) {
        setValues(fromApi(content as AlAssessmentDefaultsApi));
      }
      toast.success("AL assessment defaults saved.");
    } catch (error) {
      toast.error(
        errorMessage(error, "Failed to save AL assessment defaults.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="p-6">
        <div className="mb-1 border-b border-gray-200 pb-4">
          <div className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-violet-600" />
            <h2 className="fs-18 fw-600 mb-0 text-gray-900">
              AL Assessment Defaults
            </h2>
          </div>
          <p className="mb-0 mt-2 text-sm leading-relaxed text-gray-600">
            Default AL assessment settings for new organizations. Each
            organization&apos;s vendor admin can change them at any time. Saving
            here does not change existing organizations.
          </p>
        </div>

        {loading ? (
          <div className="flex h-10 items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
            Loading…
          </div>
        ) : (
          <>
            <AlAssessmentSettingsForm values={values} onChange={setValues} />
            <div className="mt-8 flex justify-end border-t border-gray-200 pt-6">
              <Button
                variant="primary"
                onClick={() => void handleSave()}
                disabled={saving}
                loading={saving}
                icon={<Save className="h-4 w-4" />}
              >
                Save
              </Button>
            </div>
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

export default AdminAlAssessmentDefaultsContainer;
